// Логика формы ДДС-операций админа: что считается корректной суммой, когда
// вывод залезает в чужие деньги и чем идентифицируется повторный запрос.
//
// Вынесено из MerchantDetails отдельным модулем не ради размера файла: это
// единственное место во фронтенде, где считаются деньги, и оно должно быть
// покрыто тестами независимо от рендера страницы.

import type {DebtDiscrepancyItem, MerchantResponse} from "../../../api";
import {isDebtCabinetMerchant} from "../../../api/admin/merchants";

// Зеркало серверной валидации (internal/merchant/handler.go): неотрицательное
// десятичное, не более 6 знаков после точки. Пустая строка (сброс) проверяется
// отдельно вызывающим кодом.
const USDT_AMOUNT_PATTERN = /^\d+(?:\.\d+)?$/;

// Масштаб денежных сумм на границе API — USDT/6, как currency_scale баланса.
const AMOUNT_SCALE = 6;

export function validateUsdtAmount(raw: string): string | null {
    if (!USDT_AMOUNT_PATTERN.test(raw)) return "неотрицательное число";
    const dot = raw.indexOf(".");
    if (dot >= 0 && raw.length - dot - 1 > AMOUNT_SCALE) return "не более 6 знаков после точки";
    return null;
}

// Знак денежной суммы — по строке, без Number(): суммы приходят с бэкенда
// десятичными строками именно для того, чтобы не проходить через float.
// Вызывается только на строках, уже прошедших USDT_AMOUNT_PATTERN или пришедших
// с бэка, поэтому достаточно проверить минус и наличие ненулевой цифры.
export function isPositiveAmountString(raw: string | null | undefined): boolean {
    if (!raw) return false;
    const text = raw.trim();
    if (!text || text.startsWith("-")) return false;
    return /[1-9]/.test(text);
}

// Приводит денежную строку к целому в атомарных единицах. Через BigInt, а не
// Number: двоичный float не представляет десятичные доли точно, а тут от
// сравнения зависит, увидит ли админ предупреждение о выводе. null = строка не
// похожа на неотрицательную сумму (например, отрицательный баланс) — тогда
// вызывающий код молчит, а не угадывает.
export function toAtomicUnits(raw: string): bigint | null {
    const text = raw.trim();
    if (!USDT_AMOUNT_PATTERN.test(text)) return null;
    const [whole, fraction = ""] = text.split(".");
    if (fraction.length > AMOUNT_SCALE) return null;
    return BigInt(whole + fraction.padEnd(AMOUNT_SCALE, "0"));
}

// Вывод залезает в деньги, уже обещанные взятым заявкам: amount + inFlight > balance.
// Бэкенд такой вывод пропустит (решение: леджер фиксирует реальные движения
// денег), поэтому здесь именно предупреждение, а не блокировка кнопки.
export function withdrawalExceedsFreeFunds(
    amount: string,
    knownInFlight: string,
    balance: string,
): boolean {
    const requested = toAtomicUnits(amount);
    const reserved = toAtomicUnits(knownInFlight);
    const available = toAtomicUnits(balance);
    if (requested === null || reserved === null || available === null) return false;
    return requested + reserved > available;
}

// Состояние кнопки «Вывести» в блоке «Расчёты и долг»: выключена ли она и что
// написать в подсказке. Null в title означает «подсказка не нужна».
export interface WithdrawalButtonState {
    disabled: boolean;
    title: string | null;
}

// У обычного мерчанта вывод ограничен балансом: выводить нечего, пока баланс не
// положительный, и бэкенд такой вывод отвергнет.
//
// У долгового кабинета всё наоборот, и предел с него бэкенд снял (см.
// withdrawalBoundedByBalance в internal/merchant/balance_operations.go): его
// баланс — наш минус по нашей же заявке, он отрицателен по построению, и вывод,
// уводящий долг глубже, — это штатная форма операции. Именно ею закрывается
// подтверждённое расхождение сверки: чужое поступление и потерянный вывод
// кабинета гасятся ручной проводкой с ключом коррекции. Заблокированная кнопка
// делала эту процедуру неисполнимой из карточки на любом реальном долговом
// кабинете — минус там всегда.
export function withdrawalButtonState(merchant: MerchantResponse | null): WithdrawalButtonState {
    if (!merchant) return {disabled: true, title: null};
    if (isDebtCabinetMerchant(merchant)) {
        return {
            disabled: false,
            title: "Долговой кабинет: вывод уводит долг глубже — так проводится коррекция",
        };
    }
    if (!isPositiveAmountString(merchant.amount)) {
        return {disabled: true, title: "Выводить нечего: баланс не положительный"};
    }
    return {disabled: false, title: null};
}

// Ключ идемпотентности ДДС-операции: 8..64 символов [A-Za-z0-9_:-] (см.
// operationKeyPattern в internal/merchant/handler.go). Двоеточие входит в
// алфавит, потому что тем же полем присылают ключ коррекции долгового кабинета
// вида `debt-correction:<источник>:<вид>:<id>`. UUID подходит; запасной
// вариант — для окружений без crypto.randomUUID (например http-стенда).
export function newOperationKey(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

// Зеркало серверных ограничений на operationKey — тех же, под которые попадает
// генератор выше. Нужно ровно там, где ключ приходит НЕ от генератора: процедура
// коррекции долгового кабинета печатает готовый ключ в тексте алерта, и админ
// вводит его руками. Отбить опечатку формой дешевле, чем получить 400 после
// отправки денежной формы.
const OPERATION_KEY_PATTERN = /^[A-Za-z0-9_:-]+$/;
const MIN_OPERATION_KEY_LENGTH = 8;
const MAX_OPERATION_KEY_LENGTH = 64;

// null = ключ подходит. Пустая строка сюда не приходит: пустое поле означает
// «сгенерировать», и это решает вызывающий код.
export function validateOperationKey(raw: string): string | null {
    const key = raw.trim();
    if (key.length < MIN_OPERATION_KEY_LENGTH || key.length > MAX_OPERATION_KEY_LENGTH) {
        return "от 8 до 64 символов";
    }
    if (!OPERATION_KEY_PATTERN.test(key)) {
        return "только латиница, цифры и знаки _ : -";
    }
    return null;
}

// Человеческое имя расхождения для списка «что закрываем».
//
// Текст строится здесь, а не на бэкенде, по той же причине, по которой там
// отдаются kind и суммы, а не готовая фраза: сервер отвечает за то, ЧТО
// произошло и чем это закрывается, интерфейс — за то, как это назвать. Сумма и
// направление в подписи повторяются намеренно: оператор выбирает строку из
// выпадающего списка и должен видеть последствия до того, как форма
// заполнится.
//
// Сторона названа первой и до выбора. Список один и тот же в обеих формах —
// выбор строки сам переключает направление, — но пока подпись о стороне
// молчала, открытая форма читалась как утверждение «это вывод». 04.09.2026 на
// проде так и прочитали: одиннадцать расхождений (b) закрывались пополнением, а
// спрашивали про вывод.
export function describeDiscrepancy(item: DebtDiscrepancyItem): string {
    const mark = item.alerting ? "⚠ " : "";
    const side = discrepancySide(item);
    const amount = `${item.amount} USDT`;
    switch (item.kind) {
        case "withdrawal_uncovered":
            return `${side}${mark}Вывод из кабинета не дошёл до кошелька — ${amount} (строка ${item.externalId})`;
        case "deposit_excess":
            return `${side}${mark}Поступлений больше, чем кабинет отправил — ${amount}`;
        case "order_mismatch":
            return `${side}${mark}Начисление разошлось с заявкой ${item.externalId} — ${amount}`;
        default:
            return `${side}${mark}Расхождение — ${amount}`;
    }
}

// Приставка стороны. Расхождение без направления бэкенд отдать может (оно есть
// у строк без ключа, которые в список не попадают), и врать за него нельзя —
// тогда приставки просто нет.
function discrepancySide(item: DebtDiscrepancyItem): string {
    switch (item.operationType) {
        case "topup":
            return "Пополнение · ";
        case "withdrawal":
            return "Вывод · ";
        default:
            return "";
    }
}

// Форма ручной операции по долговому кабинету: что в ней разрешено.
//
// У долгового кабинета свободной проводки нет. Деньги там двигаются только
// строкой из списка расхождений, и это не украшение интерфейса, а условие того,
// что сверка проводку увидит: она читает ручные проводки долгового кабинета по
// имени операции, которое печатает сама, и всё, названное иначе, для неё не
// существует. 03.09.2026 так и вышло дважды: расхождение закрыли руками, алерт
// продолжал гореть, а те же деньги позже провелись второй раз.
//
// Решение живёт здесь, а не в разметке страницы, по той же причине, по которой
// здесь живут суммы: это правило про деньги, и оно должно проверяться отдельно
// от рендера. Бэкенд держит ту же границу сам (ErrDebtCorrectionKeyRequired в
// internal/merchant/balance_operations.go) — форма не единственная защита, а
// объяснение для человека до отправки.

/** Текст пустого списка. Долговому кабинету без расхождений проводить нечего. */
export const NOTHING_TO_SETTLE_NOTICE = "Расхождений нет — проводить нечего.";

/** Подсказка под формой долгового кабинета. Без жаргона: ни слова про ключи. */
export const LIST_ONLY_HINT =
    "По долговому кабинету деньги двигаются только строкой из списка: проводку мимо " +
    "него сверка не увидит — расхождение останется открытым, а деньги позже " +
    "проведутся второй раз.";

/**
 * Мерчант, которому свободная проводка запрещена. Отдельная функция, потому что
 * спрашивают об этом дважды: при открытии формы (ключ не генерируется вовсе) и
 * при каждом её рендере.
 */
export function manualOperationIsListOnly(merchant: MerchantResponse | null): boolean {
    return merchant !== null && isDebtCabinetMerchant(merchant);
}

export interface ManualOperationFormInput {
    merchant: MerchantResponse | null;
    /** Открытые расхождения; у обычного мерчанта список пуст всегда. */
    discrepancies: DebtDiscrepancyItem[];
    /** Что выбрано в списке; пустая строка — не выбрано ничего. */
    pickedKey: string;
    /** Сумма как она сейчас в поле. */
    amount: string;
    /** Ключ, сгенерированный формой. У долгового кабинета его не существует. */
    generatedKey: string;
    submitting: boolean;
}

export interface ManualOperationFormState {
    /** Проводка возможна только строкой из списка: свободной формы нет. */
    listOnly: boolean;
    /** Показывать ли пункт «— просто движение денег —». */
    allowsFreeMovement: boolean;
    /** Сумму и направление вводит человек, а не строка списка. */
    amountEditable: boolean;
    /** Долговой кабинет, а закрывать нечего. */
    nothingToSettle: boolean;
    submitDisabled: boolean;
    /**
     * Имя операции, которое уйдёт с запросом. null — отправлять нечего: у
     * долгового кабинета без выбранной строки его просто нет.
     */
    operationKey: string | null;
}

export function manualOperationFormState(
    input: ManualOperationFormInput,
): ManualOperationFormState {
    const {merchant, discrepancies, pickedKey, amount, generatedKey, submitting} = input;
    const listOnly = manualOperationIsListOnly(merchant);
    const picked = discrepancies.find((item) => item.operationKey === pickedKey);
    const operationKey = listOnly
        ? picked?.operationKey ?? null
        : picked?.operationKey ?? generatedKey;
    return {
        listOnly,
        allowsFreeMovement: !listOnly,
        amountEditable: !listOnly,
        nothingToSettle: listOnly && discrepancies.length === 0,
        submitDisabled:
            submitting ||
            merchant === null ||
            !operationKey ||
            amount.trim() === "",
        operationKey: operationKey ? operationKey : null,
    };
}
